import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { customerAdminAPI } from '@/services/apis/adminAPI';
import { useNavigate } from 'react-router-dom';
import { Users, Server, TrendingUp, CreditCard } from 'lucide-react';
import { formatDateToKST } from '@/utils/dateUtils';

interface CustomerDashboardProps {
  customerId: number | null;
  onSelectCustomer: (customerId: number | null) => void;
  customers: Array<{ id: number; customer_name: string; customer_code: string }>;
}

interface DashboardStats {
  customer_id: number;
  customer_name: string;
  max_users: number;
  user_stats: {
    total: number;
    active: number;
    deactivated: number;
    pending: number;
    invited: number;
    deleted: number;
  };
  service_stats: {
    total_connected: number;
    services: Array<{
      id: number;
      name: string;
      status: string;
      connected_at: string | null;
    }>;
  };
  usage_stats: {
    // 조회가 실패하면 null 이 온다 — 0 으로 지어내지 않는다 (#103)
    total_usage: number | null;
    total_credit_used: number | null;
    total_count: number | null;
    unavailable?: boolean;
    daily_usage: Array<{
      date: string;
      usage_amount: number;
      credit_used: number;
      count: number;
    }>;
  };
  credit_info: {
    balance: number | null;
    total_charged: number | null;
    total_used: number | null;
    unavailable?: boolean;
  };
  subscription_info: {
    total: number;
    active: number;
    subscriptions: Array<{
      id: number;
      service_slug: string;
      service_name: string | null;
      status: string;
      billing_model: string;
      created_at: string | null;
    }>;
  };
}

const CustomerDashboard: React.FC<CustomerDashboardProps> = ({
  customerId,
  onSelectCustomer,
  customers,
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (customerId) {
      fetchDashboardStats();
    } else {
      setStats(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const fetchDashboardStats = async () => {
    if (!customerId) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await customerAdminAPI.getCustomerDashboard(customerId);
      setStats(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || '대시보드 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToUserManagement = () => {
    if (customerId) {
      navigate(`/users/all?subtab=all&customerId=${customerId}`);
    }
  };

  // 고객사 이름 가져오기
  const selectedCustomerName = customerId 
    ? customers.find(c => c.id === customerId)?.customer_name || '고객사'
    : null;

  // 일별 이용량 그래프 데이터 준비
  // 같은 포맷 코드가 네 군데 복사돼 있었다. 값이 없을 때 무엇을 보여줄지도 한 곳에서 정한다
  const formatAmount = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '조회 실패';
    return (value % 1 === 0 ? Math.floor(value) : value).toLocaleString();
  };

  const maxUsage = stats ? Math.max(...stats.usage_stats.daily_usage.map(d => d.count), 1) : 1;
  const maxCredit = stats ? Math.max(...stats.usage_stats.daily_usage.map(d => d.credit_used), 1) : 1;

  return (
    <div className="space-y-6">
      {/* 헤더 - 항상 동일한 레이아웃 유지 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {selectedCustomerName || '고객사 대시보드'}
          </h2>
          <p className="text-sm text-gray-500">고객사 대시보드</p>
        </div>
        <div className="flex items-center space-x-2">
          <Select
            value={customerId ? String(customerId) : 'none'}
            onValueChange={(value) => {
              if (value && value !== 'none') {
                onSelectCustomer(parseInt(value, 10));
              } else {
                onSelectCustomer(null);
              }
            }}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="고객사를 선택하세요" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">고객사 선택</SelectItem>
              {customers.map((customer) => (
                <SelectItem key={customer.id} value={String(customer.id)}>
                  {customer.customer_name} ({customer.customer_code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={handleGoToUserManagement} 
            variant="outline"
            disabled={!customerId}
          >
            사용자 관리
          </Button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <AlertMessage
          type="error"
          message={error}
          onClose={() => setError('')}
        />
      )}

      {/* 로딩 상태 */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" message="대시보드 데이터를 불러오는 중..." />
        </div>
      )}

      {/* 고객사가 선택되지 않았을 때 */}
      {!customerId && !loading && (
        <div className="flex items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-2">고객사를 선택해주세요</p>
            <p className="text-gray-400 text-sm">위의 드롭다운에서 고객사를 선택하면 대시보드가 표시됩니다.</p>
          </div>
        </div>
      )}

      {/* 고객사가 선택되었고 데이터가 있을 때만 통계 표시 */}
      {customerId && !loading && stats && (
        <>
          {/* 통계 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 사용자 통계 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500">사용자</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{stats.user_stats.total}</div>
                <div className="text-xs text-gray-500 mt-1">
                  활성: {stats.user_stats.active} / 최대: {stats.max_users}명
                </div>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
            <div className="mt-4 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">대기 중:</span>
                <span className="font-medium">{stats.user_stats.pending + stats.user_stats.invited}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">비활성:</span>
                <span className="font-medium">{stats.user_stats.deactivated}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 서비스 통계 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500">서비스</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{stats.service_stats.total_connected}</div>
                <div className="text-xs text-gray-500 mt-1">연결된 서비스</div>
              </div>
              <Server className="h-8 w-8 text-green-500" />
            </div>
            {stats.service_stats.services.length > 0 && (
              <div className="mt-4 space-y-1 text-xs">
                {stats.service_stats.services.slice(0, 3).map((service) => (
                  <div key={service.id} className="flex justify-between">
                    <span className="text-gray-500 truncate">{service.name}</span>
                    <span className={`font-medium ${
                      service.status === 'approved' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {service.status === 'approved' ? '연결' : service.status}
                    </span>
                  </div>
                ))}
                {stats.service_stats.services.length > 3 && (
                  <div className="text-gray-400">+{stats.service_stats.services.length - 3}개 더</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 이용 현황 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500">이용 현황 (30일)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">{formatAmount(stats.usage_stats.total_count)}</div>
                <div className="text-xs text-gray-500 mt-1">총 이용 횟수</div>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
            <div className="mt-4 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">사용량:</span>
                <span className="font-medium">
                  {stats.usage_stats.total_usage === null || stats.usage_stats.total_usage === undefined
                    ? '조회 실패'
                    : stats.usage_stats.total_usage.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">크레딧 사용:</span>
                <span className="font-medium">
                  {formatAmount(stats.usage_stats.total_credit_used)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 크레딧 정보 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500">크레딧</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold">
                  {formatAmount(stats.credit_info.balance)}
                </div>
                <div className="text-xs text-gray-500 mt-1">잔액</div>
              </div>
              <CreditCard className="h-8 w-8 text-orange-500" />
            </div>
            <div className="mt-4 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">충전:</span>
                <span className="font-medium">
                  {formatAmount(stats.credit_info.total_charged)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">사용:</span>
                <span className="font-medium">
                  {formatAmount(stats.credit_info.total_used)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
          </div>

          {/* 일별 이용량 그래프 */}
          <Card>
            <CardHeader>
              <CardTitle>일별 이용량 (최근 30일)</CardTitle>
              <CardDescription>일별 이용 횟수 및 크레딧 사용량</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 이용 횟수 그래프 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">이용 횟수</span>
                    <span className="text-xs text-gray-500">최대: {maxUsage}</span>
                  </div>
                  <div className="flex items-end space-x-1 h-32">
                    {stats.usage_stats.daily_usage.map((day, index) => {
                      const height = maxUsage > 0 ? (day.count / maxUsage) * 100 : 0;
                      return (
                        <div
                          key={index}
                          className="flex-1 flex flex-col items-center group relative"
                          title={`${formatDateToKST(day.date)}: ${day.count}회`}
                        >
                          <div
                            className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                            style={{ height: `${height}%`, minHeight: height > 0 ? '2px' : '0' }}
                          />
                          {index % 5 === 0 && (
                            <span className="text-xs text-gray-400 mt-1 transform -rotate-45 origin-top-left whitespace-nowrap">
                              {formatDateToKST(day.date).split(' ')[0]}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 크레딧 사용량 그래프 */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">크레딧 사용량</span>
                    <span className="text-xs text-gray-500">최대: {maxCredit}</span>
                  </div>
                  <div className="flex items-end space-x-1 h-32">
                    {stats.usage_stats.daily_usage.map((day, index) => {
                      const height = maxCredit > 0 ? (day.credit_used / maxCredit) * 100 : 0;
                      return (
                        <div
                          key={index}
                          className="flex-1 flex flex-col items-center group relative"
                          title={`${formatDateToKST(day.date)}: ${day.credit_used} 크레딧`}
                        >
                          <div
                            className="w-full bg-orange-500 rounded-t transition-all hover:bg-orange-600"
                            style={{ height: `${height}%`, minHeight: height > 0 ? '2px' : '0' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 구독 정보 */}
          {stats.subscription_info.total > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>구독 정보</CardTitle>
                <CardDescription>활성 구독: {stats.subscription_info.active} / 전체: {stats.subscription_info.total}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.subscription_info.subscriptions.map((sub) => (
                    <div
                      key={sub.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div>
                        <div className="font-medium">{sub.service_name || sub.service_slug}</div>
                        <div className="text-sm text-gray-500">
                          {sub.billing_model} • {sub.status}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {sub.created_at && formatDateToKST(sub.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerDashboard;

